import React, { useEffect, useState } from "react";
import { Navigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { STORES, detectPlatform } from "../../components/AppDownloadBanner";
import { lawyerAuth } from "../../services/lawyerApi";
import appStoreBadge from "../../assets/images/apple-dark.png";
import playStoreBadge from "../../assets/images/google-dark.png";

const BADGES = {
  ios: { src: appStoreBadge, alt: "Download on the App Store" },
  android: { src: playStoreBadge, alt: "Get it on Google Play" },
};

const PERKS = [
  { icon: "bi-telephone-inbound", text: "Receive real-time client calls" },
  { icon: "bi-camera-video", text: "Provide video consultations" },
  { icon: "bi-wallet2", text: "Earn from completed consultations" },
];

// Last setup step on phones: install the app and sign in there. The portal
// unlocks as soon as the API has seen a sign-in from the app.
const LawyerGetApp = () => {
  const { profile, refreshProfile } = useOutletContext();
  const [checking, setChecking] = useState(false);
  const platform = detectPlatform();

  // They install and sign in in the app, then switch back to this tab.
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && refreshProfile();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshProfile]);

  if (!platform || profile?.mobile_app_used_at) return <Navigate to="/lawyer/dashboard" replace />;

  const checkAgain = async () => {
    setChecking(true);
    await refreshProfile();
    setChecking(false);
    // This render's profile is stale; refreshProfile has cached the new one.
    // If the app is on record, the layout moves them on by itself.
    if (lawyerAuth.getUser()?.mobile_app_used_at) return;
    toast.info(`Sign in to the app with ${profile?.email || "your email"} first, then tap here again.`, {
      toastId: "get-app-check",
    });
  };

  // The visitor's own store first, the other one below it.
  const order = platform === "ios" ? ["ios", "android"] : ["android", "ios"];

  return (
    <div className="lwp-page lwp-get-app">
      <Seo title="Get the App | Legal Platform" noindex />
      <div className="lwp-get-app-card">
        <span className="lwp-get-app-icon">
          <i className="bi bi-phone" />
        </span>
        <h1>You&apos;re Almost Ready to Go Live!</h1>
        <p>Download the Legal Platform app to start connecting with clients and earning from legal consultations.</p>

        <ul className="lwp-get-app-perks">
          {PERKS.map((perk) => (
            <li key={perk.text}>
              <i className={`bi ${perk.icon}`} />
              {perk.text}
            </li>
          ))}
        </ul>

        <a className="lwp-btn" href={STORES[platform].url} target="_blank" rel="noopener noreferrer">
          <i className={`bi ${platform === "ios" ? "bi-apple" : "bi-google-play"}`} />
          Download the app
        </a>

        <span className="lwp-get-app-divider">or get it from</span>

        <div className="lwp-get-app-badges">
          {order.map((key) => (
            <a key={key} href={STORES[key].url} target="_blank" rel="noopener noreferrer" aria-label={BADGES[key].alt}>
              <img src={BADGES[key].src} alt={BADGES[key].alt} />
            </a>
          ))}
        </div>
      </div>

      <button type="button" className="lwp-get-app-link" onClick={checkAgain} disabled={checking}>
        {checking ? "Checking…" : "I already have the app — continue on web"}
      </button>
    </div>
  );
};

export default LawyerGetApp;
